import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from '../../../context/LanguageContext';
import { useSystemSettings } from '../../../context/SystemSettingsContext';
import { WidgetBody, hasWidgetBody } from './DashboardWidgets';
import './WidgetDashboard.css';

/* ===== 사출기 대시보드 > 「대시보드」(위젯) 탭 — PNG 시안「전사 _ 베트남 공장 _ 사출기 #1 _ 대시보드 _ {보기 모드, 편집 모드, 위젯 추가 모달, 위젯 크기 선택}」 =====
 * 위젯 목록·저장(자동 저장 effect)·위젯 내용(renderWidget)·사용자 모듈 빌더는 Machine.jsx 에 그대로 두고,
 * 그리드/툴바/편집 모드/위젯 추가 모달/크기 선택만 이 파일이 그린다. 스타일은 wd- 접두어만 쓴다.
 *
 * - 그리드: 4열 × 4행 = 16칸. 위젯은 slot(1~16, 왼쪽 위부터) + size(1x1 / 2x1 / 1x2 / 2x2) 로 자리를 잡는다(기존 저장 형식 그대로).
 * - 편집 모드: 위젯을 끌어 빈 칸에 놓기, 크기 배지를 눌러 들어가는 다음 크기로 변경, X 로 제거, 레이아웃 초기화.
 *   시안의 "모서리로 크기 조절" 은 배지 클릭으로 대신한다.
 * - 위젯 추가 모달: 검색 · 카테고리 칩 · 배치 여부(같은 종류는 한 개만, 사용자 모듈은 여러 개) · 배치된 위젯 수.
 * - 자동 갱신 주기는 시스템 설정(refreshInterval)의 값을 그대로 표시한다. */

const COLS = 4;
const ROWS = 4;
const SLOT_COUNT = COLS * ROWS;
const SIZES = { '1x1': [1, 1], '2x1': [2, 1], '1x2': [1, 2], '2x2': [2, 2] };
const SIZE_ORDER = ['1x1', '2x1', '1x2', '2x2'];

/* 위젯 종류 — type 은 Machine.jsx renderWidget 이 아는 이름, key 는 번역 키(widget.types.*), cat 은 모달 카테고리 */
const WIDGET_TYPES = [
  { type: '온도', key: 'temperature', icon: 'thermostat', cat: 'process' },
  { type: '압력', key: 'pressure', icon: 'compress', cat: 'process' },
  { type: '에너지', key: 'energy', icon: 'bolt', cat: 'equipment' },
  { type: '기본정보', key: 'basicInfo', icon: 'info', cat: 'equipment' },
  { type: '알람이력', key: 'alarmHistory', icon: 'notification_important', cat: 'alarm' },
  { type: '기본사출정보', key: 'injectionInfo', icon: 'straighten', cat: 'equipment' },
  /* PNG 시안에 있는 위젯 3종 — 각각 사이클·SPC·양/불 집계 API 를 쓴다 (machine/DashboardWidgets) */
  { type: '사이클', key: 'cycle', icon: 'autorenew', cat: 'process' },
  { type: 'SPC', key: 'spc', icon: 'assessment', cat: 'process' },
  { type: '양불', key: 'quality', icon: 'check_circle', cat: 'quality' },
  { type: '사용자모듈', key: 'custom', icon: 'dashboard_customize', cat: 'user', multiple: true }
];
const CATEGORIES = ['all', 'process', 'equipment', 'quality', 'alarm'];

/* slot(1~16) + size → 차지하는 칸 목록. 격자 밖으로 나가면 null */
const cellsOf = (slot, size) => {
  const dims = SIZES[size] || SIZES['1x1'];
  const [w, h] = dims;
  const row = Math.floor((slot - 1) / COLS);
  const col = (slot - 1) % COLS;
  if (col + w > COLS || row + h > ROWS) return null;
  const cells = [];
  for (let r = 0; r < h; r += 1) for (let c = 0; c < w; c += 1) cells.push((row + r) * COLS + (col + c) + 1);
  return cells;
};

function WidgetDashboard({
  equipmentId, widgets, setWidgets, renderWidget,
  isModalOpen, setIsModalOpen, selectedSlot, setSelectedSlot, onOpenCustomBuilder
}) {
  const { t } = useTranslation();
  const { settings } = useSystemSettings();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.widget.dashboardNew.${key}`, params), [t]);
  const typeName = useCallback((def) => t(`pages.monitoring.machine.widget.types.${def.key}.name`), [t]);

  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sizeStep, setSizeStep] = useState(null);      // 크기 선택 단계의 위젯 정의
  const [chosenSize, setChosenSize] = useState('2x1');  // 시안 기본 선택: 2×1 (추천)
  const [dragId, setDragId] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  /* ---------------- 배치 계산 ---------------- */
  const occupiedBy = useCallback((excludeId) => {
    const map = {};
    widgets.forEach((w) => {
      if (w.id === excludeId || !w.slot) return;
      (cellsOf(w.slot, w.size) || []).forEach((c) => { map[c] = w.id; });
    });
    return map;
  }, [widgets]);
  const fits = useCallback((slot, size, excludeId) => {
    const cells = cellsOf(slot, size);
    if (!cells) return false;
    const occ = occupiedBy(excludeId);
    return cells.every((c) => occ[c] == null);
  }, [occupiedBy]);
  const firstFreeSlot = useCallback((size) => {
    for (let s = 1; s <= SLOT_COUNT; s += 1) if (fits(s, size)) return s;
    return null;
  }, [fits]);

  const placed = useMemo(() => {
    const occ = occupiedBy(null);
    const items = widgets.filter((w) => w.slot && cellsOf(w.slot, w.size));
    const emptySlots = [];
    for (let s = 1; s <= SLOT_COUNT; s += 1) if (occ[s] == null) emptySlots.push(s);
    return { items, emptySlots };
  }, [widgets, occupiedBy]);

  const typeDef = (type) => WIDGET_TYPES.find((d) => d.type === type) || null;
  const widgetTitle = (w) => {
    if (w.type === '사용자모듈' && w.config?.title) return w.config.title;
    const suffix = { '온도': 'temperature', '압력': 'pressure', '에너지': 'energy' }[w.type];
    return suffix ? t(`pages.monitoring.machine.widget.titleSuffix.${suffix}`) : (typeDef(w.type) ? typeName(typeDef(w.type)) : w.type);
  };
  const widgetNote = (w) => { const d = typeDef(w.type); return d ? tr(`note.${d.cat}`) : ''; };

  /* ---------------- 편집 동작 ---------------- */
  const removeWidget = (id) => setWidgets(widgets.filter((w) => w.id !== id));
  const cycleSize = (w) => {
    const start = SIZE_ORDER.indexOf(w.size);
    for (let i = 1; i <= SIZE_ORDER.length; i += 1) {
      const next = SIZE_ORDER[(start + i) % SIZE_ORDER.length];
      if (next !== w.size && fits(w.slot, next, w.id)) {
        setWidgets(widgets.map((x) => (x.id === w.id ? { ...x, size: next } : x)));
        return;
      }
    }
  };
  const moveWidget = (id, slot) => {
    const w = widgets.find((x) => x.id === id);
    if (!w || !fits(slot, w.size, id)) return;
    setWidgets(widgets.map((x) => (x.id === id ? { ...x, slot } : x)));
  };
  const resetLayout = () => {
    if (window.confirm(tr('resetConfirm'))) setWidgets([]);
  };

  /* ---------------- 모달 ---------------- */
  const openModal = (slot) => {
    setSelectedSlot(slot ?? firstFreeSlot('1x1'));
    setSizeStep(null);
    setSearch('');
    setCategory('all');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setSizeStep(null);
  };
  const isPlaced = (def) => !def.multiple && widgets.some((w) => w.type === def.type);
  const catCount = (cat) => WIDGET_TYPES.filter((d) => cat === 'all' || d.cat === cat).length;
  const listItems = WIDGET_TYPES.filter((d) => (category === 'all' || d.cat === category)
    && (!search.trim() || `${typeName(d)} ${t(`pages.monitoring.machine.widget.types.${d.key}.desc`)}`.toLowerCase().includes(search.trim().toLowerCase())));
  const startAdd = (def) => {
    if (def.multiple) {
      /* 사용자 모듈은 Machine.jsx 의 빌더가 크기·차트를 정한다 */
      closeModal();
      if (onOpenCustomBuilder) onOpenCustomBuilder();
      return;
    }
    setChosenSize('2x1');
    setSizeStep(def);
  };
  const targetSlotFor = (size) => (selectedSlot && fits(selectedSlot, size) ? selectedSlot : firstFreeSlot(size));
  const confirmAdd = () => {
    if (!sizeStep) return;
    const slot = targetSlotFor(chosenSize);
    if (!slot) return;
    setWidgets([...widgets, { id: Date.now(), type: sizeStep.type, size: chosenSize, slot }]);
    closeModal();
  };

  /* ---------------- 렌더 ---------------- */
  const renderToolbar = () => (
    editMode ? (
      <div className="wd-toolbar">
        <div className="wd-toolbar-left">
          <span className="wd-pill"><i />{tr('editMode')}</span>
          <span className="wd-hint">{tr('editHint')}</span>
        </div>
        <div className="wd-toolbar-right">
          <button type="button" className="wd-btn wd-btn-outline" onClick={resetLayout} disabled={widgets.length === 0}>{tr('resetLayout')}</button>
          <button type="button" className="wd-btn wd-btn-primary" onClick={() => setEditMode(false)}>{tr('doneEdit')}</button>
        </div>
      </div>
    ) : (
      <div className="wd-toolbar">
        <div className="wd-toolbar-left">
          <h3 className="wd-title">{tr('myDashboard')}</h3>
          <span className="wd-sub">{tr('subtitle', { n: placed.items.length })}</span>
        </div>
        <div className="wd-toolbar-right">
          <span className="wd-hint">{tr('autoRefresh', { v: settings?.refreshInterval || '—' })}</span>   {/* 시스템 설정 refreshInterval */}
          <button type="button" className="wd-btn wd-btn-primary" onClick={() => { setEditMode(true); openModal(null); }}>
            <span className="material-symbols-outlined" aria-hidden="true">add</span>{tr('addEdit')}
          </button>
        </div>
      </div>
    )
  );

  const placement = (slot, size) => {
    const [w, h] = SIZES[size] || SIZES['1x1'];
    const row = Math.floor((slot - 1) / COLS);
    const col = (slot - 1) % COLS;
    return { gridColumn: `${col + 1} / span ${w}`, gridRow: `${row + 1} / span ${h}` };
  };

  const renderGrid = () => (
    <div className={`wd-grid ${editMode ? 'wd-grid-edit' : ''}`}>
      {placed.items.map((w) => (
        <section
          key={w.id}
          className={`wd-card wd-card-${w.size}`}
          style={placement(w.slot, w.size)}
          draggable={editMode}
          onDragStart={(e) => { setDragId(w.id); e.dataTransfer.effectAllowed = 'move'; }}
          onDragEnd={() => { setDragId(null); setDragOverSlot(null); }}
        >
          <div className="wd-card-head">
            {editMode && <span className="material-symbols-outlined wd-handle" aria-hidden="true">drag_indicator</span>}
            <h4 className="wd-card-title">{widgetTitle(w)}</h4>
            {editMode ? (
              <div className="wd-card-tools">
                <button type="button" className="wd-size-badge" title={tr('sizeBadgeHint')} onClick={() => cycleSize(w)}>{w.size.replace('x', '×')}</button>
                <button type="button" className="wd-remove" aria-label={tr('remove')} onClick={() => removeWidget(w.id)}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            ) : (
              <span className="wd-card-note">{widgetNote(w)}</span>
            )}
          </div>
          {/* 기본 위젯은 새 본문(DashboardWidgets), 사용자 모듈은 Machine.jsx 의 기존 renderWidget */}
          <div className="widget-content wd-body">
            {hasWidgetBody(w.type) ? <WidgetBody widget={w} equipmentId={equipmentId} /> : renderWidget(w)}
          </div>
        </section>
      ))}
      {placed.emptySlots.map((slot) => {
        const dragging = dragId ? widgets.find((x) => x.id === dragId) : null;
        const canDrop = dragging ? fits(slot, dragging.size, dragging.id) : false;
        return (
          <button
            key={`empty-${slot}`}
            type="button"
            className={`wd-empty ${dragOverSlot === slot && canDrop ? 'wd-empty-over' : ''}`}
            style={placement(slot, '1x1')}
            onClick={() => { if (!editMode) setEditMode(true); openModal(slot); }}
            onDragOver={(e) => { if (canDrop) { e.preventDefault(); setDragOverSlot(slot); } }}
            onDragLeave={() => setDragOverSlot((s) => (s === slot ? null : s))}
            onDrop={(e) => { e.preventDefault(); if (dragId) moveWidget(dragId, slot); setDragId(null); setDragOverSlot(null); }}
          >
            <span className="material-symbols-outlined" aria-hidden="true">add</span>
            {editMode && <span className="wd-empty-text">{tr('addWidget')}</span>}
          </button>
        );
      })}
    </div>
  );

  const renderSizeStep = () => (
    <>
      <div className="wd-modal-head">
        <h2 className="wd-modal-title">{tr('sizeTitle')} <em>· {typeName(sizeStep)}</em></h2>
        <button type="button" className="wd-modal-close" aria-label={tr('close')} onClick={closeModal}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
      <p className="wd-modal-hint">{tr('sizeHint')}</p>
      <div className="wd-size-options">
        {SIZE_ORDER.map((size) => {
          const ok = !!targetSlotFor(size);
          return (
            <button
              key={size}
              type="button"
              className={`wd-size-option ${chosenSize === size ? 'active' : ''}`}
              disabled={!ok}
              title={ok ? undefined : tr('noFit')}
              onClick={() => setChosenSize(size)}
            >
              <span className={`wd-size-preview wd-size-preview-${size}`} />
              <span className="wd-size-label">
                {t(`pages.monitoring.machine.widget.modal.size${size}`)}
                {chosenSize === size && <span className="material-symbols-outlined wd-size-check">check</span>}
              </span>
              <span className="wd-size-desc">{tr(`sizeDesc.${size}`)}</span>
            </button>
          );
        })}
      </div>
      <div className="wd-modal-foot">
        <button type="button" className="wd-btn wd-btn-outline" onClick={() => setSizeStep(null)}>{t('pages.monitoring.machine.widget.modal.back')}</button>
        <button type="button" className="wd-btn wd-btn-primary" disabled={!targetSlotFor(chosenSize)} onClick={confirmAdd}>
          {tr('addWithSize', { size: chosenSize.replace('x', '×') })}
        </button>
      </div>
    </>
  );

  const renderPicker = () => (
    <>
      <div className="wd-modal-head">
        <h2 className="wd-modal-title">{t('pages.monitoring.machine.widget.modal.title')}</h2>
        <button type="button" className="wd-modal-close" aria-label={tr('close')} onClick={closeModal}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
      <label className="wd-search">
        <span className="material-symbols-outlined" aria-hidden="true">search</span>
        <input type="text" value={search} placeholder={tr('searchPlaceholder')} onChange={(e) => setSearch(e.target.value)} />
      </label>
      <div className="wd-chips-row">
        <div className="wd-chips">
          {CATEGORIES.map((cat) => (
            <button key={cat} type="button" className={`wd-chip ${category === cat ? 'active' : ''}`} onClick={() => setCategory(cat)}>
              {tr(`cat.${cat}`)} {catCount(cat)}
            </button>
          ))}
        </div>
        <span className="wd-hint">{tr('catHint')}</span>
      </div>
      <div className="wd-list">
        {listItems.length === 0 && <div className="wd-list-empty">{tr('noResults')}</div>}
        {listItems.map((def) => {
          const done = isPlaced(def);
          return (
            <div key={def.type} className={`wd-item ${done ? 'wd-item-placed' : ''}`}>
              <span className="wd-item-icon"><span className="material-symbols-outlined">{def.icon}</span></span>
              <div className="wd-item-info">
                <div className="wd-item-name">{typeName(def)}<em className="wd-tag">{tr(`cat.${def.cat}`)}</em></div>
                <div className="wd-item-desc">{t(`pages.monitoring.machine.widget.types.${def.key}.desc`)}</div>
              </div>
              {done ? (
                <span className="wd-placed"><span className="material-symbols-outlined">check</span>{tr('placed')}</span>
              ) : (
                <button type="button" className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => startAdd(def)}>
                  <span className="material-symbols-outlined" aria-hidden="true">add</span>{tr('add')}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="wd-modal-foot">
        <span className="wd-hint">{tr('placedCount', { n: placed.items.length, total: SLOT_COUNT })}</span>
        <button type="button" className="wd-btn wd-btn-primary" onClick={closeModal}>{tr('close')}</button>
      </div>
    </>
  );

  return (
    <div className="wd-root">
      {renderToolbar()}
      {renderGrid()}
      {isModalOpen && (
        <div className="wd-overlay" onClick={closeModal}>
          <div className={`wd-modal ${sizeStep ? 'wd-modal-size' : ''}`} onClick={(e) => e.stopPropagation()}>
            {sizeStep ? renderSizeStep() : renderPicker()}
          </div>
        </div>
      )}
    </div>
  );
}

export default WidgetDashboard;
